import { useState } from "react";
import { Link } from "wouter";
import { Loader2, Shield, CheckCircle, AlertCircle, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

function SierraAccountSection({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery<{
    sierraUsername: string | null;
    changeCount: number;
    entitlement: {
      status: string;
      authorizationEndDate: string | null;
      adminSuspended: boolean;
      lastSyncAt: string | null;
    } | null;
    configured: boolean;
  }>({
    queryKey: ["/api/sierra/account"],
    staleTime: 30_000,
  });

  function startEdit() {
    setInput(data?.sierraUsername ?? "");
    setEditing(true);
  }

  async function save() {
    const trimmed = input.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const res = await apiRequest("PUT", "/api/sierra/account", { sierraUsername: trimmed });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed to save");
      queryClient.invalidateQueries({ queryKey: ["/api/sierra/account"] });
      setEditing(false);
      toast({ title: "Sierra Chart account updated", description: "Your access is being re-synced." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const entStatus = data?.entitlement?.status;
  const entitlementBadge = () => {
    if (data?.entitlement?.adminSuspended) return <Badge variant="destructive">Suspended</Badge>;
    if (entStatus === "authorized") return <Badge className="bg-green-600 text-white">Active</Badge>;
    if (entStatus === "deauthorized") return <Badge variant="secondary">Deauthorized</Badge>;
    if (entStatus === "sync_pending") return <Badge variant="outline">Syncing…</Badge>;
    if (entStatus === "pending") return <Badge variant="outline">Pending</Badge>;
    return null;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          Sierra Chart Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-9 w-60" />
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Enter the Sierra Chart account name shown under{" "}
              <strong>Help → About</strong> in Sierra Chart (labeled "Registered To:"). This activates
              your indicators inside Sierra Chart automatically.
            </p>

            {editing ? (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="scUsername" className="text-sm">Sierra Chart Account Name</Label>
                  <Input
                    id="scUsername"
                    value={input}
                    onChange={(e) => setInput(e.target.value.toUpperCase())}
                    placeholder="e.g. JOHN12345"
                    className="mt-1 max-w-xs font-mono"
                    onKeyDown={(e) => e.key === "Enter" && save()}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Changing this will remove access from the old account and transfer it to the new one.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={save} disabled={saving || !input.trim()}>
                    {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving…</> : "Save"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  {data?.sierraUsername ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{data.sierraUsername}</span>
                        {entitlementBadge()}
                      </div>
                      {entStatus === "authorized" && data.entitlement?.authorizationEndDate && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Access through{" "}
                          {new Date(data.entitlement.authorizationEndDate).toLocaleDateString()}
                        </p>
                      )}
                      {data?.entitlement?.adminSuspended && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Account suspended — contact support
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Not set — your indicators won't activate until you link your Sierra Chart account.
                    </p>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={startEdit}>
                  <Edit2 className="w-3 h-3 mr-1" />
                  {data?.sierraUsername ? "Change" : "Set account"}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, isLoading, logout, isLoggingOut } = useAuth();

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Account Settings" description="Manage your account settings and profile information." noIndex />
        <h1 className="text-2xl sm:text-3xl font-bold mb-8">Account Settings</h1>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-60" />
                </div>
              </div>
              <Separator className="my-4" />
              <div className="space-y-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-96" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-96" />
              </div>
              <Separator className="my-4" />
              <Skeleton className="h-9 w-32" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Account Settings" description="Manage your account settings and profile information." noIndex />
        <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-settings-title">
          Account Settings
        </h1>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <h2 className="font-semibold text-lg mb-4">Sign In to Access Settings</h2>
              <p className="text-muted-foreground mb-6">
                You need to sign in to your account to manage your settings.
              </p>
              <Link href="/auth">
                <Button>Sign In</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Account Settings" description="Manage your account settings and profile information." noIndex />
      <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-settings-title">
        Account Settings
      </h1>

      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <Avatar className="h-16 w-16">
                {user.profileImageUrl && (
                  <AvatarImage src={user.profileImageUrl} alt={`${user.firstName} ${user.lastName}`} />
                )}
                <AvatarFallback className="text-lg font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-semibold" data-testid="text-user-name">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <Separator className="mb-6" />

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">First Name</label>
                <p className="text-lg">{user.firstName || "-"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Last Name</label>
                <p className="text-lg">{user.lastName || "-"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <p className="text-lg" data-testid="text-user-email">
                  {user.email}
                </p>
              </div>
            </div>

            <Separator className="mb-6" />

            <Button
              size="lg"
              variant="destructive"
              disabled={isLoggingOut}
              onClick={() => logout()}
              data-testid="button-settings-sign-out"
            >
              {isLoggingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing Out...
                </>
              ) : (
                "Sign Out"
              )}
            </Button>
          </CardContent>
        </Card>

        <SierraAccountSection userId={user.id} />
      </div>
    </div>
  );
}
